<script setup lang="ts">
import { $getChangeRequestSubtaskComments } from '@/api/changeRequests'
import { useSnackbarStore } from '@/store/snackbar'
import type { ChangeRequest, ChangeRequestSubtaskComment } from '@/models/changeRequest'
import {TaggedUser} from "@/models/activity-timeline";

interface IProps {
  changeRequestSubtaskId: number | string | null | undefined
}

const props = withDefaults(defineProps<IProps>(), {
  changeRequestSubtaskId: null,
})

interface CustomEvents {
  (e: 'onAddComment'): void
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const route = useRoute()
const router = useRouter()

const placeholder = 'Write a comment...'

const isMounted = ref<boolean>(false)

// crs - Change Request Subtask
const crsComments = ref<ChangeRequestSubtaskComment[]>([])
const loading = ref<boolean>(false)

onMounted(() => {
  if (props.changeRequestSubtaskId) {
    getCRSComments()
  }
  isMounted.value = true
})

const getCRSComments = async () => {
  if (!props.changeRequestSubtaskId) return

  loading.value = true

  try {
    const res = await $getChangeRequestSubtaskComments({
      cr_subtask: props.changeRequestSubtaskId
    })
    crsComments.value = res.data.results
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

// Function to highlight tagged users
const highlightTaggedUsers = (content: string, taggedUsers: TaggedUser[], commentId: string): string => {
  // Replace tagged users with highlighted format
  taggedUsers.forEach(taggedUser => {
    content = content.replaceAll(
      `@${taggedUser.id}`,
      `<span class="highlight-tag-user tag-user-${commentId}-${taggedUser.id}">@${taggedUser.first_name} ${taggedUser.last_name}</span>`
    )
  })
  return content
}
</script>

<template>
  <div>
    <VCard style="max-height: 80vh; overflow-y: scroll">
      <template #title>
        <div class="d-flex align-center">
          Change Request Subtask Notes
        </div>
      </template>
      <VCardText>
        <VOverlay
          v-model="loading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <VRow style="margin-bottom: 70px">
          <VCol
            v-for="(crsComment, crsCommentIdx) in crsComments"
            :key="crsCommentIdx"
            cols="12"
          >
            <VCard variant="tonal" class="pa-3">
              <div class="d-flex align-start gap-3">
                <VAvatar color="primary" size="36">
                  <VIcon icon="tabler-user" size="20" />
                </VAvatar>

                <div class="flex-grow-1">
                  <div class="d-flex align-center mb-1">
                    <span class="font-weight-medium text-body-1">
                      {{ crsComment.user_name }}
                    </span>
                    <VSpacer />
                    <span class="text-caption text-medium-emphasis">
                      <VIcon icon="tabler-clock" size="14" class="mr-1" />
                      {{ crsComment.created_display }}
                    </span>
                  </div>

                  <div
                    v-html="highlightTaggedUsers(crsComment.comment, crsComment.tagged_users_info, crsComment.id)"
                    class="text-body-2"
                  />
                  <VMenu
                    v-for="(taggedUser, taggedUserKey) in crsComment.tagged_users"
                    :key="taggedUserKey"
                    :activator="`.tag-user-${crsComment.id}-${taggedUser.id}`"
                    location="top center"
                    open-on-hover
                    open-delay="100"
                    close-delay="0"
                    offset="5"
                    class="default-tooltip"
                  >
                    <span class="px-2 text-center">
                      {{ taggedUser.email }}
                    </span>
                  </VMenu>
                </div>
              </div>
            </VCard>
          </VCol>
          <VCol
            v-if="crsComments?.length === 0"
            class="text-center"
          >
            <VAlert show>
              Notes not found
            </VAlert>
          </VCol>
        </VRow>
        <div class="change-request-subtask-notes-bottom-card">
          <VCard class="py-3">
            <CommentTagInput
              :is-change-request-subtask-comment="true"
              :change-request-subtask-id="changeRequestSubtaskId ?? null"
              :placeholder="'Write a note...'"
              @onAddComment="getCRSComments(); emit('onAddComment')"
            />
          </VCard>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.change-request-subtask-notes-bottom-card {
  transition: width 0.3s ease, right 0.3s ease; /* Transition width and right properties */
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0; /* Initially hide */
  .add-comment-btn-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%); // centering
  }
}
</style>
